"use client"

import { DayPicker, type DayPickerSingleProps } from "react-day-picker"
import { ChevronLeft, ChevronRight } from "../../icons"
import { disabledVariants } from "../../styles"
import { classNames } from "../../utils"
import { buttonVariants } from "../Button"

export type CalendarProps = Omit<
  DayPickerSingleProps,
  "mode" | "selected" | "onSelect"
> & {
  selected?: Date
  onSelect?: (date: Date | undefined) => void
}
const CalendarIconLeft = ({
  className,
  ...props
}: React.ComponentProps<typeof ChevronLeft>) => (
  <ChevronLeft
    className={classNames("-translate-x-px", className)}
    {...props}
  />
)

const CalendarIconRight = ({
  className,
  ...props
}: React.ComponentProps<typeof ChevronRight>) => (
  <ChevronRight
    className={classNames("translate-x-px", className)}
    {...props}
  />
)

export function Calendar({
  className,
  showOutsideDays = true,
  selected,
  onSelect,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      className={className}
      classNames={{
        months: classNames(
          "flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0",
        ),
        month: classNames("space-y-4"),
        caption: classNames("relative flex items-center justify-center pt-1"),
        caption_label: classNames("text-sm font-medium"),
        nav: classNames("flex items-center space-x-1"),
        nav_button: classNames(
          buttonVariants({ variant: "secondary" }),
          "size-7 p-0",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: classNames("w-full border-collapse space-y-1"),
        head_row: classNames("flex"),
        head_cell: classNames(
          "w-9 rounded-md text-sm font-normal text-text-secondary",
        ),
        row: classNames("mt-2 flex w-full"),
        cell: classNames(
          "relative p-0 text-center font-mono text-sm focus-within:relative focus-within:z-20",
          "aria-selected:rounded-md aria-selected:bg-text-primary",
          "[&:has([aria-selected].day-outside)]:bg-bg-additional-1",
        ),
        day: classNames(
          buttonVariants({ variant: "ghost" }),
          "size-9 p-0 font-normal",
          "focus-visible:border focus-visible:border-border-1",
        ),
        day_selected: classNames(
          "!bg-text-primary !font-medium !text-bg-primary hover:!bg-text-primary hover:!text-bg-primary",
        ),
        day_today: classNames("bg-bg-additional-1 !font-medium"),
        day_disabled: disabledVariants({ disabled: true }),
        day_hidden: classNames("invisible"),
      }}
      components={{
        IconLeft: CalendarIconLeft,
        IconRight: CalendarIconRight,
      }}
      mode="single"
      onSelect={date => {
        onSelect?.(date)
      }}
      selected={selected}
      showOutsideDays={showOutsideDays}
      {...props}
    />
  )
}
